import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { debounce, throttle } from "lodash-es";

import { apiClient } from "@/core/api/ApiClient";
import { TenantToTenantCountersDto } from "@/core/api/generated/v1";

import { AppDispatch, AppThunkAction } from "..";

interface TenantToTenantState {
  loading: {
    getCounters?: boolean;
  };
  counters?: TenantToTenantCountersDto;
}

const initialState: TenantToTenantState = {
  loading: {},
  counters: undefined,
};

const slice = createSlice({
  name: "tenantToTenant",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<TenantToTenantState["loading"]>) => {
      state.loading = {
        ...state.loading,
        ...action.payload,
      };
    },
    _countersFetched: (state, action: PayloadAction<TenantToTenantCountersDto>) => {
      state.counters = action.payload;
    },
  },
});

export const { setLoading, _countersFetched } = slice.actions;

export const tenantToTenantReducer = slice.reducer;

const _getCounters = async (dispatch: AppDispatch): Promise<TenantToTenantCountersDto> => {
  dispatch(
    setLoading({
      getCounters: true,
    }),
  );
  try {
    const response = await apiClient.tenantToTenantApi.apiV1TenantToTenantCountersGet({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    });
    await dispatch(_countersFetched(response.data));
    return response.data;
  } finally {
    dispatch(
      setLoading({
        getCounters: false,
      }),
    );
  }
};
const _getCountersDebounce = debounce(_getCounters, 1000, {
  leading: false,
  trailing: true,
});
const _getCountersThrottle = throttle(_getCounters, 1000, {
  leading: true,
  trailing: false,
});

export const getCounters =
  (): AppThunkAction<Promise<TenantToTenantCountersDto>> => async (dispatch) => {
    return await _getCounters(dispatch);
  };
export const getCountersDebounce =
  (): AppThunkAction<Promise<TenantToTenantCountersDto>> => async (dispatch) => {
    return (await _getCountersDebounce(dispatch))!;
  };
export const getCountersThrottle =
  (): AppThunkAction<Promise<TenantToTenantCountersDto>> => async (dispatch) => {
    return (await _getCountersThrottle(dispatch))!;
  };
